/**
 * 🟥 CUADRO NEGRO Panel de Suscripción de Alta Conversión (feb-2026).
 *
 * Overlay modal completo (no widget lateral) que reemplaza al
 * SubscriptionWidget tradicional cuando un usuario Free solicita el
 * upgrade. Está optimizado para conversión:
 *
 *   - Título emocional: "Desbloqueá todo el poder de Lyon IA"
 *   - 2 tarjetas: Mensual ($9) vs Anual ($86 — pago único)
 *   - Lista de beneficios destacados.
 *   - CTAs grandes con gradiente Premium amber→fuchsia.
 *   - Sello de confianza: "Cancelá cuando quieras · Pago seguro con
 *     Stripe · Sin compromisos".
 *   - Recuerda el último plan elegido (localStorage).
 *   - Bloquea interacción con el fondo (backdrop click cierra modal).
 *   - Premium users → no se monta.
 *
 * Mecánica de apertura:
 *   - `openSubscriptionPanel()` dispara `damyxia:open-subscription-panel`.
 *   - Este componente vive global en App.js y se monta una sola vez.
 *
 * Mecánica de checkout:
 *   - Click en CTA → POST /api/billing/checkout {plan: "monthly" | "yearly"}.
 *   - Recibe {url} → window.location.href = url (Stripe Checkout).
 *   - Tras el pago, Stripe redirige a /suscripcion/completada.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Crown, Check, Loader2, Lock, Shield, Sparkles,
  MessageSquare, Image as ImageIcon, Zap, Star, Wand2,
} from 'lucide-react';
import { API } from '../config';
import { PREMIUM_BENEFITS } from '../constants/premiumBenefits';

const STORAGE_KEY = 'damyxia.lastChosenPlan';

// Mapeo icon → benefit. Los textos vienen de la constante oficial y
// NO se reescriben aquí. Si un beneficio no aparece en este mapa, se
// renderiza con el icono por defecto Sparkles.
const BENEFIT_ICONS = {
  "Chat ilimitado con Lyon IA": MessageSquare,
  "Acciones avanzadas del Builder": Zap,
  "Style Packs y recetas exclusivas": Sparkles,
  "Inferencia de estilos y análisis inteligente": Wand2,
  "Acceso prioritario a nuevas funciones": Star,
  "Imágenes y mensajes ilimitados": ImageIcon,
};

const SubscriptionUpgradeOverlay = ({ isOpen, onClose, isPremium = false }) => {
  // 🟥 Cuadro Negro §6.2: Premium users NO ven este panel.
  const blocked = isPremium === true;

  const [selectedPlan, setSelectedPlan] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === 'yearly' ? 'yearly' : 'monthly';
    } catch {
      return 'monthly';
    }
  });
  const [loading, setLoading] = useState(false);

  // Recordar la elección del usuario.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, selectedPlan); } catch { /* ignore */ }
  }, [selectedPlan]);

  // ESC cierra.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Bloquear scroll del fondo mientras está abierto.
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  if (!isOpen || blocked) return null;

  const handleCheckout = async (plan) => {
    if (loading) return;
    setLoading(true);
    setSelectedPlan(plan);
    console.info('[DamyxIA] Iniciando checkout →', plan);
    try {
      const res = await axios.post(
        `${API}/billing/checkout`,
        { plan, origin_url: window.location.origin },
        { withCredentials: true, timeout: 15000 },
      );
      const url = res?.data?.url;
      const err = res?.data?.error;
      console.info('[DamyxIA] Checkout response →', { hasUrl: !!url, err });
      if (url) {
        // 🟪 P25.35 · Fix del iframe (aporte de Emmy) — el widget del Workspace
        // funciona porque usa `window.open` PRIMERO, saltando el iframe del
        // editor Emergent. Alineamos aquí la misma estrategia:
        //   1) window.open(url, '_blank') → nueva pestaña (bypass iframe)
        //   2) si el popup fue bloqueado → window.top.location.href (rompe iframe)
        //   3) último fallback: window.location.href
        //   4) watchdog 4s: toast + copia URL si nada funcionó
        console.info('[DamyxIA] Iniciando checkout →', plan);
        const beforeHref = window.location.href;
        let navigated = false;

        try {
          const w = window.open(url, '_blank', 'noopener,noreferrer');
          if (w) {
            navigated = true;
            setLoading(false);
            toast.success('Abrimos la página de pago en otra pestaña 👆', { duration: 4500 });
          }
        } catch { /* popup bloqueado, seguimos con fallbacks */ }

        if (!navigated) {
          try {
            if (window.top && window.top !== window.self) {
              window.top.location.href = url;
            } else {
              window.location.href = url;
            }
            navigated = true;
          } catch {
            try { window.location.href = url; navigated = true; } catch { /* ignore */ }
          }
        }

        setTimeout(() => {
          if (window.location.href === beforeHref && !navigated) {
            setLoading(false);
            toast.error(
              'La página de pago no cargó (bloqueo del navegador o iframe). Copiá el enlace y abrilo manualmente.',
              { duration: 8000 },
            );
            try { navigator.clipboard.writeText(url); } catch { /* ignore */ }
            console.warn('[DamyxIA] Stripe redirect bloqueado; url:', url);
          }
        }, 4000);
        return;
      }
      if (err === 'already_premium') {
        toast.info('Ya tenés Plan Premium activo. ¡Gracias por apoyar DamyxIA!', { duration: 4500 });
        onClose?.();
      } else {
        toast.error(`No pudimos iniciar el pago (${err || 'sin URL'}). Intentá de nuevo en unos segundos.`, { duration: 5000 });
      }
    } catch (e) {
      console.error('[DamyxIA] Checkout error:', e?.response?.status, e?.response?.data, e);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.response?.data?.error;
      const msg = status
        ? `Error ${status} — ${detail || 'servicio de pagos no disponible'}.`
        : 'No pudimos conectar con el servicio de pagos. Reintentá en un momento.';
      toast.error(msg, { duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  const planCards = [
    {
      id: 'monthly',
      label: 'Plan Mensual',
      price: 9,
      perWhat: '/mes',
      hint: 'Suscripción mensual flexible.',
      badge: null,
      cta: 'Elegir Plan Mensual',
    },
    {
      id: 'yearly',
      label: 'Plan Anual',
      price: 86,
      perWhat: '/año',
      hint: 'Pago único, no se renueva. Ahorrá $22 vs mensual.',
      badge: 'Pago único',
      cta: 'Elegir Plan Anual',
    },
  ];

  return createPortal(
    <div
      className="fixed inset-0 z-[10020] flex items-stretch md:items-center justify-center sm:p-3 md:p-6 bg-black/80 backdrop-blur-md animate-in fade-in duration-150"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="subscription-overlay"
    >
      <div
        className="relative w-full md:max-w-3xl md:rounded-3xl border border-amber-500/30 bg-gradient-to-b from-zinc-900 via-zinc-950 to-zinc-900 shadow-2xl shadow-amber-500/10 overflow-hidden animate-in zoom-in-95 duration-150 h-full md:h-auto md:max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top border glow */}
        <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-amber-400 via-fuchsia-400 to-amber-400" />

        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          data-testid="subscription-upgrade-close-btn"
          className="absolute top-3 right-3 p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Scrollable body — padding 20px en mobile, mayor en desktop. */}
        <div className="flex-1 overflow-y-auto px-5 py-7 md:px-8 md:pt-8 md:pb-6">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 mb-3 rounded-2xl bg-gradient-to-br from-amber-500/25 to-fuchsia-500/15 border border-amber-500/40 text-amber-300">
              <Crown className="w-7 h-7" />
            </div>
            <h2
              className="text-2xl sm:text-3xl font-bold text-white leading-tight"
              data-testid="subscription-upgrade-title"
            >
              Desbloqueá todo el poder de Lyon IA
            </h2>
            <p className="text-sm sm:text-base text-zinc-400 mt-2 max-w-xl mx-auto">
              Acciones avanzadas, estilos, recetas y más — sin límites.
            </p>
          </div>

          {/* Benefits — orden EXACTO del cuadro negro oficial.
              §2.4: lista vertical en mobile, ícono check a la izquierda,
              sin overflow horizontal. break-words evita romper layouts. */}
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
            {PREMIUM_BENEFITS.map((text) => {
              const Icon = BENEFIT_ICONS[text] || Sparkles;
              return (
                <li
                  key={text}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/5 text-sm text-zinc-300 break-words"
                >
                  <div className="shrink-0 w-7 h-7 rounded-md bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-300">
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <span className="min-w-0">{text}</span>
                </li>
              );
            })}
          </ul>

          {/* Plan cards — §2.3: apiladas en mobile, lado a lado en sm+. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            {planCards.map((p) => {
              const active = selectedPlan === p.id;
              return (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => setSelectedPlan(p.id)}
                  data-testid="plan-card"
                  data-plan-id={p.id}
                  className={[
                    'group relative text-left rounded-2xl p-4 border transition-all',
                    active
                      ? 'border-amber-400/70 bg-gradient-to-b from-amber-500/10 to-fuchsia-500/5 shadow-lg shadow-amber-500/10'
                      : 'border-zinc-700 bg-zinc-900/60 hover:border-zinc-600 hover:bg-zinc-900',
                  ].join(' ')}
                >
                  {p.badge && (
                    <span className="absolute -top-2 right-3 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gradient-to-r from-emerald-400 to-cyan-400 text-zinc-950 shadow">
                      {p.badge}
                    </span>
                  )}
                  <div className="flex items-start gap-3">
                    <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${
                      active
                        ? 'bg-gradient-to-br from-amber-500/30 to-fuchsia-500/20 border border-amber-500/40 text-amber-300'
                        : 'bg-zinc-800 border border-zinc-700 text-zinc-400'
                    }`}>
                      <Crown className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs uppercase tracking-wide text-zinc-400">{p.label}</p>
                      <p className="mt-0.5 text-2xl font-bold text-white">
                        ${p.price}
                        <span className="text-sm text-zinc-400 font-normal">{p.perWhat}</span>
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">{p.hint}</p>
                    </div>
                    <div
                      className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1 ${
                        active ? 'border-amber-400 bg-amber-400' : 'border-zinc-600'
                      }`}
                    >
                      {active && <Check className="w-3 h-3 text-zinc-950" />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer sticky con CTA + trust — §2.2: botones siempre visibles. */}
        <div
          className="shrink-0 border-t border-zinc-800 bg-zinc-950/95 backdrop-blur px-5 py-4 md:px-8 md:py-5"
          data-testid="subscription-upgrade-footer"
        >
          {/* Primary CTA (selected plan) */}
          <button
            type="button"
            disabled={loading}
            onClick={() => handleCheckout(selectedPlan)}
            data-testid="premium-cta"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl text-base font-semibold text-zinc-950 bg-gradient-to-r from-amber-400 to-fuchsia-400 hover:from-amber-300 hover:to-fuchsia-300 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-amber-500/20 transition-all"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Sparkles className="w-5 h-5" />
            )}
            {planCards.find((p) => p.id === selectedPlan)?.cta || 'Obtener Premium'}
          </button>

          <p className="text-center text-xs text-zinc-500 mt-3">
            o tocá una tarjeta arriba para cambiar de plan
          </p>

          {/* Trust row */}
          <div
            className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-zinc-500"
            data-testid="subscription-upgrade-trust"
          >
            <span className="inline-flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5 text-emerald-400" /> Cancelá cuando quieras
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5 text-emerald-400" /> Pago seguro con Stripe
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5 text-emerald-400" /> Sin compromisos
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SubscriptionUpgradeOverlay;
